import { useCallback, useEffect, useRef, useState } from 'react';
import { getActivityImageReadUrl } from 'services/image-upload';

/**
 * Resolves a displayable image URL: external `url`, or presigned read URL for private R2 (`file_path`).
 * When `file_path` is set, refetches before the signed read URL expires.
 */
export function useActivityImageDisplaySource(
  activityId: string | undefined,
  url: string | undefined,
  filePath: string | undefined
) {
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const mountedRef = useRef(true);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const fetchReadUrl = useCallback(
    async (isBackgroundRefresh = false) => {
      if (!activityId || !filePath) return;
      if (!isBackgroundRefresh) {
        setIsLoading(true);
      }
      setError(false);
      try {
        const { readUrl, expiresInSeconds } = await getActivityImageReadUrl(
          activityId,
          filePath
        );
        if (!mountedRef.current) return;
        setDisplayUrl(readUrl);
        clearRefresh();
        const ttl = Math.max(60, expiresInSeconds - 60);
        const ms = Math.max(5_000, ttl * 1000);
        refreshTimerRef.current = setTimeout(() => {
          void fetchReadUrl(true);
        }, ms);
      } catch {
        if (mountedRef.current && !isBackgroundRefresh) {
          setError(true);
          setDisplayUrl(null);
        }
      } finally {
        if (mountedRef.current && !isBackgroundRefresh) {
          setIsLoading(false);
        }
      }
    },
    [activityId, filePath, clearRefresh]
  );

  useEffect(() => {
    mountedRef.current = true;
    clearRefresh();
    setError(false);
    const trimmed = url?.trim();

    if (filePath) {
      if (!activityId) {
        setDisplayUrl(null);
        setIsLoading(false);
        setError(true);
        return () => {
          mountedRef.current = false;
          clearRefresh();
        };
      }
      setDisplayUrl(null);
      void fetchReadUrl(false);
    } else if (trimmed) {
      setDisplayUrl(trimmed);
      setIsLoading(false);
    } else {
      setDisplayUrl(null);
      setIsLoading(false);
    }

    return () => {
      mountedRef.current = false;
      clearRefresh();
    };
  }, [activityId, filePath, url, fetchReadUrl, clearRefresh]);

  return { displayUrl, isLoading, error };
}
